/** ethminions.php / minions_watcher.js 와 동일한 랜덤니스 → 출발·줄수·결과 */

export type MinionsStart = 'left' | 'right';
export type MinionsResult = 'odd' | 'even';

export interface DerivedRound {
  start: MinionsStart;
  lines: 3 | 4;
  result: MinionsResult;
  sBit: number;
  lBit: number;
  last: string;
  secondLast: string | null;
}

const LADDER_LEFT_X = 30;
const LADDER_RIGHT_X = 170;
const LADDER_TOP_Y = 90;
const LADDER_BOTTOM_Y = 250;

export function normalizeRandomnessHex(input: string | null | undefined): string {
  if (input == null || input === '') return '';
  const raw = String(input).trim().toLowerCase();
  if (!raw) return '';
  return raw.startsWith('0x') ? raw.slice(2) : raw;
}

function getHexParityBit(ch: string): number | null {
  if (!ch) return null;
  const c = ch.toLowerCase();
  if ('02468ace'.includes(c)) return 0;
  if ('13579bdf'.includes(c)) return 1;
  return null;
}

export function deriveByRandomness(randomnessHex: string): DerivedRound | null {
  const hex = normalizeRandomnessHex(randomnessHex);
  if (!hex) return null;
  const last = hex[hex.length - 1];
  const secondLast = hex.length >= 2 ? hex[hex.length - 2] : null;
  const sBit = getHexParityBit(last);
  const lBit = secondLast != null ? getHexParityBit(secondLast) : null;
  if (sBit === null || lBit === null) return null;
  const start: MinionsStart = sBit === 0 ? 'left' : 'right';
  const lines: 3 | 4 = lBit === 0 ? 3 : 4;
  const result: MinionsResult = (sBit ^ lBit) === 1 ? 'odd' : 'even';
  return { start, lines, result, sBit, lBit, last, secondLast };
}

export function computeResultFromStartAndLines(
  start: MinionsStart,
  lines: number
): MinionsResult {
  const startIsLeft = start === 'left';
  const endIsLeft = lines % 2 === 0 ? startIsLeft : !startIsLeft;
  return endIsLeft ? 'odd' : 'even';
}

function getRungYs(lines: 3 | 4): number[] {
  if (lines === 3) return [130, 170, 210];
  return [120, 155, 190, 225];
}

export interface Bridge {
  y: number;
  cross: boolean;
}

export function generateBridgeLogic(
  _start: MinionsStart,
  lines: 3 | 4,
  _result: MinionsResult,
  _seedHash?: string
): Bridge[] {
  const ys = getRungYs(lines);
  return ys.map((y) => ({ y, cross: true }));
}

/** Pixi Graphics 용 — 세로선 + 가로(교차) 곡선 */
export function buildLadderStrokeCommands(
  start: MinionsStart,
  lines: 3 | 4,
  result: MinionsResult,
  hash?: string
): { rails: [number, number, number, number][]; bridges: Bridge[] } {
  const bridges = generateBridgeLogic(start, lines, result, hash);
  const rails: [number, number, number, number][] = [
    [LADDER_LEFT_X, LADDER_TOP_Y, LADDER_LEFT_X, LADDER_BOTTOM_Y],
    [LADDER_RIGHT_X, LADDER_TOP_Y, LADDER_RIGHT_X, LADDER_BOTTOM_Y],
  ];
  return { rails, bridges };
}

export function buildWinnerPolyline(
  start: MinionsStart,
  lines: 3 | 4,
  result: MinionsResult,
  hash?: string
): number[] {
  const bridges = generateBridgeLogic(start, lines, result, hash);
  const startIsLeft = start === 'left';
  let currentX = startIsLeft ? LADDER_LEFT_X : LADDER_RIGHT_X;
  const pts: number[] = [currentX, LADDER_TOP_Y];
  for (const b of bridges) {
    const y = b.y;
    pts.push(currentX, y);
    if (b.cross) {
      currentX = currentX === LADDER_LEFT_X ? LADDER_RIGHT_X : LADDER_LEFT_X;
      pts.push(currentX, y);
    }
  }
  pts.push(currentX, LADDER_BOTTOM_Y);
  return pts;
}

export const ladderGeom = {
  LADDER_LEFT_X,
  LADDER_RIGHT_X,
  LADDER_TOP_Y,
  LADDER_BOTTOM_Y,
};

/** `LadderBoard.layoutInRect` 와 동일 — 보드 앵커(디스플레이) 내부 배치 */
const BOARD_LAYOUT = { designW: 200, designH: 280, pad: 8 } as const;

export function ladderBoardLayoutInRect(w: number, h: number): {
  s: number;
  bx: number;
  by: number;
} {
  const { designW, designH, pad } = BOARD_LAYOUT;
  const availW = Math.max(80, w - pad * 2);
  const availH = Math.max(80, h - pad * 2);
  const s = Math.min(availW / designW, availH / designH);
  const bx = (w - designW * s) / 2;
  const by = pad + (availH - designH * s) / 2;
  return { s, bx, by };
}

/** 세로 사다리선 끝 네 점 — 보드 앵커 로컬 (0…w, 0…h) */
export function ladderRailEndsInParentRect(w: number, h: number): {
  leftTop: { x: number; y: number };
  rightTop: { x: number; y: number };
  bottomLeft: { x: number; y: number };
  bottomRight: { x: number; y: number };
} {
  const { s, bx, by } = ladderBoardLayoutInRect(w, h);
  const p = (lx: number, ly: number) => ({
    x: bx + lx * s,
    y: by + ly * s,
  });
  return {
    leftTop: p(LADDER_LEFT_X, LADDER_TOP_Y),
    rightTop: p(LADDER_RIGHT_X, LADDER_TOP_Y),
    bottomLeft: p(LADDER_LEFT_X, LADDER_BOTTOM_Y),
    bottomRight: p(LADDER_RIGHT_X, LADDER_BOTTOM_Y),
  };
}
